import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Rate } from 'antd';

import { FormRenderer } from '@/shared/cms/form';
import type { Form, ReviewForm } from '@/shared/cms/shared';
import { useLanguage } from '@/shared/lib/hooks';
import { Modal } from '@/shared/ui/modal';

import { useGetReviewsContentQuery } from '../../api/reviews.api';

import cls from './reviews-modal.module.css';

interface ReviewsModalProps {
    isCollapsed?: boolean;
    userEmail?: string | undefined;
}

export const ReviewsModal = memo(
    ({ isCollapsed = false, userEmail }: ReviewsModalProps) => {
        const [language] = useLanguage();
        const [isOpen, setIsOpen] = useState(false);
        const { t } = useTranslation('translation');
        const rateUsText = t('reviews.trigger');

        const { data } = useGetReviewsContentQuery({
            locale: language,
        });

        const handleSubmitSuccess = useCallback(() => {
            setIsOpen(false);
        }, []);

        const renderTrigger = () => {
            if (isCollapsed) {
                return (
                    <button
                        aria-label={rateUsText}
                        className={cls.triggerCollapsed}
                        onClick={() => setIsOpen(true)}
                        type="button"
                    >
                        <Rate
                            allowHalf
                            className={cls.cardRatingCollapsed}
                            disabled
                            value={4.5}
                        />
                    </button>
                );
            }

            return (
                <button
                    aria-label={rateUsText}
                    className={cls.triggerExpanded}
                    onClick={() => setIsOpen(true)}
                    type="button"
                >
                    <div className={cls.cardContent}>
                        <div className={cls.cardTitle}>{rateUsText}</div>

                        <div className={cls.cardScore}>
                            <Rate
                                allowHalf
                                className={cls.cardRatingExpanded}
                                disabled
                                value={4.5}
                            />
                            <span>
                                {t('reviews.score', {
                                    score: '4,9',
                                    total: '5',
                                })}
                            </span>
                        </div>
                    </div>
                </button>
            );
        };

        const renderContent = (reviews: ReviewForm) => {
            const { form } = reviews;

            return (
                <>
                    {reviews.description && (
                        <p className={cls.description}>{reviews.description}</p>
                    )}

                    {form && (
                        <FormRenderer
                            form={form as Form}
                            onSubmitSuccess={handleSubmitSuccess}
                            userEmail={userEmail}
                        />
                    )}
                </>
            );
        };

        if (!data) return null;

        return (
            <>
                {renderTrigger()}

                <Modal
                    content={renderContent(data)}
                    isOpen={isOpen}
                    onClose={() => setIsOpen(false)}
                    title={data.title}
                />
            </>
        );
    },
);

ReviewsModal.displayName = 'ReviewsModal';
